import { assertNever } from "@redotech/util/type";
import * as z from "zod/v4";
import { disputeEvidenceSchema_ } from "./dispute-evidence-definition";

export const disputeSource = ["shopify"] as const;

export type DisputeSource = (typeof disputeSource)[number];

export const disputeType = ["chargeback", "inquiry"] as const;

export type DisputeType = (typeof disputeType)[number];

export const disputeReason = [
  "bank_cannot_process",
  "credit_not_processed",
  "customer_initiated",
  "debit_not_authorized",
  "duplicate",
  "fraudulent",
  "general",
  "incorrect_account_details",
  "insufficient_funds",
  "noncompliant",
  "product_not_received",
  "product_unacceptable",
  "subscription_cancelled",
  "unrecognized",
] as const;

export type DisputeReason = (typeof disputeReason)[number];

export const disputeStatus = [
  "needs_response",
  "under_review",
  "charge_refunded",
  "accepted",
  "won",
  "lost",
] as const;

export type DisputeStatus = (typeof disputeStatus)[number];

const disputeSchema_ = z.object({
  id: z.coerce.bigint(),
  accountId: z.coerce.bigint(),
  source: z.enum(disputeSource),
  sourceDisputeId: z.string(),
  sourceOrderId: z.string().nullish(),
  type: z.enum(disputeType),
  status: z.enum(disputeStatus),
  reason: z.enum(disputeReason).nullish(),
  networkReasonCode: z.string().nullish(),
  amount: z.coerce.number(),
  currency: z.string(),
  evidenceDueBy: z.coerce.date().nullish(),
  evidenceSentOn: z.coerce.date().nullish(),
  finalizedOn: z.coerce.date().nullish(),
  initiatedAt: z.coerce.date(),
  rawDispute: z.unknown(),
  aiEvidenceGeneratedAt: z.coerce.date().nullish(),
  autoEvidenceDisabled: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullish(),
});
export const disputeSchema: z.ZodType<Dispute> = disputeSchema_;
export interface Dispute extends z.infer<typeof disputeSchema_> {}

const disputeViewSchema_ = disputeSchema_.extend({
  orderName: z.string().nullish(),
  customerName: z.string().nullish(),
  evidence: disputeEvidenceSchema_.nullish(),
});

export const disputeViewSchema: z.ZodType<DisputeView> = disputeViewSchema_;
export interface DisputeView extends z.infer<typeof disputeViewSchema_> {}

// Timeline steps

export const disputeTimelineStep = [
  "DISPUTE_CREATED",
  "SUBMIT_EVIDENCE",
  "BANK_REVIEW",
  "DISPUTE_SETTLED",
] as const;

export type DisputeTimelineStep = (typeof disputeTimelineStep)[number];

export function getActiveTimelineStep(
  status: DisputeStatus,
): DisputeTimelineStep {
  switch (status) {
    case "needs_response":
      return "SUBMIT_EVIDENCE";
    case "under_review":
      return "BANK_REVIEW";
    case "won":
    case "lost":
    case "accepted":
    case "charge_refunded":
      return "DISPUTE_SETTLED";
    default:
      assertNever(status);
  }
}

export interface InsertDisputeInput {
  accountId: bigint;
  source: DisputeSource;
  sourceDisputeId: string;
  sourceOrderId?: string | null;
  type: DisputeType;
  status: DisputeStatus;
  reason?: DisputeReason | null;
  networkReasonCode?: string | null;
  amount: number;
  currency: string;
  evidenceDueBy?: Date | null;
  evidenceSentOn?: Date | null;
  finalizedOn?: Date | null;
  initiatedAt: Date;
  rawDispute: unknown;
}
